import { uiText, useUiLanguage, currentLocale } from '@/i18n/interface';

import React from 'react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Clock, Users, Calendar, Video, Monitor, MapPin } from 'lucide-react';

interface TrainingSession {
  id: string;
  title: string;
  date: string;
  start_time: string;
  end_time: string;
  description: string | null;
  max_participants: number | null;
  instructor_id: string | null;
  created_at: string;
  attendance_count?: number;
  age_group?: 'all' | 'kids' | 'adults';
  session_type?: 'in_person' | 'online' | 'hybrid';
  online_meeting_url?: string | null;
  meeting_platform?: string | null;
  meeting_password?: string | null;
}

interface SessionInfoProps {
  session: (TrainingSession | any) & { type: 'training' | 'promotion' };
  userRole: string | undefined;
  userResponse: boolean | null;
}

const SessionInfo: React.FC<SessionInfoProps> = ({ session, userRole, userResponse }) => {
  useUiLanguage();
  const formatTime = (timeString: string) => {
    return new Date(`1970-01-01T${timeString}`).toLocaleTimeString(currentLocale(), {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDateToDDMMYYYY = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const getAgeGroupBadge = (ageGroup: string | undefined) => {
    if (!ageGroup || ageGroup === 'all') return null;

    return (
      <Badge
        variant="outline"
        className={ageGroup === 'kids' ? 'border-blue-500 text-blue-700 bg-blue-50' : 'border-green-500 text-green-700 bg-green-50'}
      >
        {ageGroup === 'kids' ? uiText("Kids (5-13)") : uiText("Adults (18+)")}
      </Badge>
    );
  };

  const getSessionTypeBadge = (sessionType: string | undefined) => {
    if (!sessionType || sessionType === 'in_person') return null;

    const config = {
      online: { icon: Video, color: 'border-purple-500 text-purple-700 bg-purple-50', label: uiText("Online") },
      hybrid: { icon: Monitor, color: 'border-orange-500 text-orange-700 bg-orange-50', label: uiText("Hybrid") }
    };

    const { icon: Icon, color, label } = config[sessionType as keyof typeof config] || config.online;

    return (
      <Badge variant="outline" className={color}>
        <Icon className="h-3 w-3 mr-1" />
        {label}
      </Badge>
    );
  };

  const getMeetingPlatformIcon = (platform: string | undefined) => {
    switch (platform) {
      case 'zoom':
        return '📹';
      case 'google_meet':
        return '🎥';
      case 'teams':
        return '💼';
      default:
        return '🔗';
    }
  };

  return (
    <div className="flex-1">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <h4 className="font-semibold text-gray-900">{session.title}</h4>
        <Badge variant={session.type === 'promotion' ? 'default' : 'secondary'}>
          {session.type === 'promotion' ? uiText("Belt Test") : uiText("Training")}
        </Badge>
        {getSessionTypeBadge((session as TrainingSession).session_type)}
        {getAgeGroupBadge(session.age_group)}
        {userRole === 'student' && session.type === 'training' && userResponse !== null && (
          <Badge variant={userResponse ? 'default' : 'destructive'}>
            {userResponse ? uiText("Will Attend") : uiText("Will Not Attend")}
          </Badge>
        )}
      </div>

      <div className="space-y-1 text-sm text-gray-600">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          <span>{formatDateToDDMMYYYY(session.date)}</span>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4" />
          <span>
            {formatTime(session.start_time)} - {formatTime(session.end_time)}
          </span>
        </div>
        {session.max_participants && session.type === 'training' && (
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            <span>
              {(session as TrainingSession).attendance_count || 0}/{session.max_participants} {' '}{uiText("participants")}</span>
          </div>
        )}
        {session.description && (
          <p className="mt-2 text-gray-700">{session.description}</p>
        )}

        {/* Meeting Link for Online/Hybrid Sessions */}
        {((session as TrainingSession).session_type === 'online' || (session as TrainingSession).session_type === 'hybrid') &&
         (session as TrainingSession).online_meeting_url && (
          <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-lg">{getMeetingPlatformIcon((session as TrainingSession).meeting_platform)}</span>
                <div>
                  <p className="text-sm font-medium text-blue-900">
                    {(session as TrainingSession).meeting_platform ?
                      uiText("{{value0}}{{value1}} Meeting", { value0: (session as TrainingSession).meeting_platform?.charAt(0).toUpperCase(), value1: (session as TrainingSession).meeting_platform?.slice(1).replace('_', ' ') }) :
                      uiText("Online Meeting")}
                  </p>
                  {(session as TrainingSession).meeting_password && (
                    <p className="text-xs text-blue-700">{uiText("Password:")}{' '}{(session as TrainingSession).meeting_password}</p>
                  )}
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => window.open((session as TrainingSession).online_meeting_url!, '_blank')}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Video className="h-4 w-4 mr-1" />
                {uiText("Join")}</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionInfo;
